import React, {useState} from 'react';

import {useSelector} from 'react-redux';

import {TeamBadgeList} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/TeamBadgeList';
import {
  excludeTeams,
  getCommonDocument,
} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/utils';
import {CurrentUserSelector} from 'app/current-user/api';
import {useTeams} from 'app/teams/api';
import {
  getPoliciesUrlForTeam,
  getProceduresUrlForTeam,
  getProcessesUrlForTeam,
  getSignoffUrlForTeam,
} from 'app/urls';
import {UNLIMITED_PAGE_SIZE} from 'app/utils/constants';
import {gt, ngt} from 'app/utils/i18n';

import type {BaseActionableDocumentType} from 'app/general/types';
import type {BasicTeamSerializedType} from 'sweet-types';

type Props = {
  document: BaseActionableDocumentType;
  signoffEnabledTeamUrls: Set<string>;
  contentType: 'procedure' | 'policy' | 'proceduregroup';
};

export function SignoffTeams({
  document,
  signoffEnabledTeamUrls,
  contentType,
}: Props) {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  const {id: currentUserId, teams: userTeams} = useSelector((state) => {
    const data = CurrentUserSelector(state).data!;
    return {id: data.id, teams: data.teams};
  });

  // Teams this document is attached to (directly or via a folder/process),
  // scoped to the current user so teams they have no access to are excluded.
  const {data: teamsResponse, isLoading} = useTeams({
    page: '1',
    page_size: `${UNLIMITED_PAGE_SIZE}`,
    document_url: getCommonDocument(document).url,
    user_id: `${currentUserId}`,
  });

  if (isLoading) {
    return null;
  }
  const documentTeams = teamsResponse?.results ?? [];

  const teamsWithSignoffsAll = documentTeams.filter((team) =>
    signoffEnabledTeamUrls.has(team.url)
  );

  const disabledSignoffsTeams = documentTeams.filter(
    (team) => !signoffEnabledTeamUrls.has(team.url)
  );
  const docNotAttachedTeams = excludeTeams(userTeams, documentTeams);

  return (
    <div>
      <TeamBadgeList
        text={ngt(
          'Members of this team will be required to sign off this document.',
          'Members of these teams will be required to sign off this document.',
          teamsWithSignoffsAll.length
        )}
        allTeams={teamsWithSignoffsAll}
        collapse={true}
      />

      {docNotAttachedTeams.length > 0 || disabledSignoffsTeams.length > 0 ? (
        <button
          type="button"
          className="btn btn-link"
          onClick={() => {
            setIsCollapsed(!isCollapsed);
          }}
        >
          <small>
            {isCollapsed ? gt('Show other teams') : gt('Collapse')}{' '}
            <i className={`fa fa-chevron-${isCollapsed ? 'down' : 'up'}`} />
          </small>
        </button>
      ) : null}

      {isCollapsed ? null : (
        <>
          <TeamBadgeList
            text={gt(
              'The "When managers choose" Sign Off rule is not enabled for these teams'
            )}
            allTeams={disabledSignoffsTeams}
            urlExtractor={(team: BasicTeamSerializedType) =>
              getSignoffUrlForTeam(team)
            }
          />
          <TeamBadgeList
            text={gt('This document is not attached to these teams.')}
            urlExtractor={(team: BasicTeamSerializedType) =>
              contentType === 'procedure'
                ? getProceduresUrlForTeam(team)
                : contentType === 'proceduregroup'
                ? getProcessesUrlForTeam(team)
                : contentType === 'policy'
                ? getPoliciesUrlForTeam(team)
                : team.html_url
            }
            allTeams={docNotAttachedTeams}
          />
        </>
      )}
    </div>
  );
}
